import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Typography,
	makeStyles,
	Chip,
	Paper,
	Tabs,
	Tab,
	IconButton,
	Tooltip,
} from "@material-ui/core";
import { MessageType } from "../utils/constants";
import {
	Email,
	Sms,
	Chat,
	Code,
	Visibility,
	Fullscreen,
	PhotoLibrary,
} from "@material-ui/icons";
import AceEditor from "react-ace";
import { setupMessageTemplatesMappableFieldsHighlighting } from "../utils/aceEditorUtils";
import FullScreenEditor from "./FullScreenEditor";
import TextareaWithAutocompletion from "./TextareaWithAutocompletion";
import MediaLibraryModal from "../../../../Common/MediaLibrary/MediaLibraryModal";
import { getMappableFieldsForContext } from "../utils/textareaAutocompletion";
import { MappableFieldsInfo } from "./MappableFieldsInfo";

import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-text";
import "ace-builds/src-noconflict/theme-textmate";
import "ace-builds/src-noconflict/ext-language_tools";

const useStyles = makeStyles((theme) => ({
	editorContainer: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(2),
	},
	header: {
		display: "flex",
		alignItems: "center",
		marginBottom: theme.spacing(1),
	},
	headerLabel: {
		fontWeight: 500,
		marginRight: theme.spacing(1),
	},
	textarea: {
		width: "100%",
		minHeight: 200,
		padding: theme.spacing(2),
		fontSize: "14px",
		fontFamily: "inherit",
		lineHeight: 1.6,
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 4,
		resize: "vertical",
		"&:focus": {
			outline: "none",
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
		},
	},
	errorText: {
		color: "#C4455B",
		fontSize: "0.75rem",
		marginTop: theme.spacing(0.5),
	},
	editorWrapper: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 4,
		overflow: "hidden",
	},
	tabsContainer: {
		backgroundColor: theme.palette.background.default,
	},
	previewContainer: {
		padding: theme.spacing(2),
		minHeight: 200,
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 4,
		marginTop: theme.spacing(1),
		overflow: "auto",
	},
	htmlPreview: {
		width: "100%",
		height: "100%",
		minHeight: 200,
	},
	chip: {
		height: 20,
		fontSize: "0.75rem",
	},
}));

const MessageBodyEditor = ({
	messageType,
	value,
	onChange,
	onBlur,
	resources,
	error,
	disabled,
	dealerGroupId,
	markPreviewAsViewed,
	showPreview,
	useCase,
}) => {
	const classes = useStyles();
	const [viewMode, setViewMode] = useState(0); // 0 = edit, 1 = preview
	const [fullScreenOpen, setFullScreenOpen] = useState(false);
	const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
	const editorRef = useRef(null);
	const highlighterRef = useRef(null);

	const [initialContent, setInitialContent] = useState(value || "");
	const [hasCheckedPreview, setHasCheckedPreview] = useState(false);

	const useCaseId = useCase?.useCase;

	useEffect(() => {
		if (messageType !== MessageType.email || !editorRef.current?.editor) {
			return;
		}

		// Clean up any existing highlighter first
		if (highlighterRef.current) {
			highlighterRef.current.dispose();
			highlighterRef.current = null;
		}

		highlighterRef.current = setupMessageTemplatesMappableFieldsHighlighting(
			editorRef.current.editor,
			useCaseId,
			messageType
		);

		// Cleanup function to remove markers when component unmounts
		return () => {
			if (highlighterRef.current) {
				highlighterRef.current.dispose();
				highlighterRef.current = null;
			}
		};
	}, [messageType, useCaseId]);

	useEffect(() => {
		setInitialContent(value || "");
		setHasCheckedPreview(false);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [messageType]);

	useEffect(() => {
		if (showPreview && messageType === MessageType.email && viewMode !== 1) {
			setViewMode(1);
		}
	}, [showPreview, messageType, viewMode]);

	const editorInfo = React.useMemo(() => {
		switch (messageType) {
			case MessageType.email:
				return {
					label: resources["EmailBody"],
					icon: <Email fontSize="small" />,
					chipLabel: "HTML",
					placeholder: resources["EnterEmailBody"],
				};
			case MessageType.sms:
				return {
					label: resources["SMSBody"],
					icon: <Sms fontSize="small" />,
					chipLabel: "SMS",
					placeholder: resources["EnterSMSBody"],
				};
			case MessageType.chatMessage:
				return {
					label: resources["ChatBody"],
					icon: <Chat fontSize="small" />,
					chipLabel: "Chat",
					placeholder: resources["EnterChatMessage"],
				};
			default:
				return;
		}
	}, [messageType, resources]);

	const handleAceChange = (newValue) => {
		onChange(newValue);

		if (newValue !== initialContent && hasCheckedPreview) {
			setHasCheckedPreview(false);
		}
	};

	const handleTabChange = (event, newValue) => {
		setViewMode(newValue);

		// Mark preview as viewed when switching to preview tab
		if (newValue === 1 && messageType === MessageType.email) {
			markPreviewAsViewed();
		}

		// Refresh the Ace editor when switching back to edit mode
		if (
			newValue === 0 &&
			messageType === MessageType.email &&
			editorRef.current?.editor
		) {
			setTimeout(() => {
				const editor = editorRef.current.editor;
				editor.resize(true);
				editor.renderer.updateFull(true);
				editor.focus();
			}, 0);
		}
	};

	const handleOpenFullScreen = () => {
		setFullScreenOpen(true);
	};

	const handleCloseFullScreen = () => {
		setFullScreenOpen(false);
		if (onBlur) {
			onBlur();
		}
	};

	const handleOpenMediaLibrary = () => {
		setMediaLibraryOpen(true);
	};

	const handleCloseMediaLibrary = () => {
		setMediaLibraryOpen(false);
	};

	const handleBlur = () => {
		if (onBlur) {
			onBlur();
		}
	};

	const availableFields = getMappableFieldsForContext(
		useCase?.useCase,
		messageType
	);

	useEffect(() => {
		if (messageType !== MessageType.email) {
			return;
		}

		// Create a style element to hide ace editor readonly tooltips
		const style = document.createElement("style");
		style.id = "hide-ace-readonly-tooltip";
		style.textContent = `
			.ace_tooltip {
				display: none !important;
			}
		`;

		if (!document.getElementById("hide-ace-readonly-tooltip")) {
			document.head.appendChild(style);
		}

		return () => {
			const existingStyle = document.getElementById(
				"hide-ace-readonly-tooltip"
			);
			if (existingStyle) {
				existingStyle.remove();
			}
		};
	}, [messageType]);

	const isDisabled = disabled || !useCase?.useCase;

	return (
		<Box className={classes.editorContainer}>
			<Box className={classes.header}>
				<Typography variant="subtitle1" className={classes.headerLabel}>
					{editorInfo.label}
					<span style={{ color: "#f44336", marginLeft: "4px" }}>*</span>
				</Typography>

				<Chip
					size="small"
					icon={editorInfo.icon}
					label={editorInfo.chipLabel}
					color="primary"
				/>
			</Box>

			{!useCase?.useCase && (
				<Typography
					variant="body2"
					color="textSecondary"
					style={{
						fontStyle: "italic",
						marginBottom: 8,
					}}
				>
					{resources["PleaseSelectUseCase"]}
				</Typography>
			)}

			{messageType === MessageType.email ? (
				<Box>
					<Paper className={classes.tabsContainer}>
						<Box display="flex" alignItems="center">
							<Box flex={1}>
								<Tabs
									value={viewMode}
									onChange={handleTabChange}
									indicatorColor="primary"
									textColor="primary"
									variant="fullWidth"
								>
									<Tab
										icon={<Code />}
										label={resources["HTMLEditor"]}
										disabled={disabled}
										id="html-editor-tab"
									/>
									<Tab
										icon={<Visibility />}
										label={resources["Preview"]}
										id="preview-tab"
									/>
								</Tabs>
							</Box>
							<>
								<Tooltip title={resources["MediaLibraryTooltip"]}>
									<IconButton
										onClick={handleOpenMediaLibrary}
										disabled={disabled}
										style={{ marginRight: 8 }}
										id="media-library-button"
									>
										<PhotoLibrary />
									</IconButton>
								</Tooltip>
								{viewMode === 0 && (
									<Tooltip title={resources["OpenFullScreenTooltip"]}>
										<IconButton
											onClick={handleOpenFullScreen}
											disabled={isDisabled}
											style={{ marginRight: 8 }}
											id="full-screen-button"
										>
											<Fullscreen />
										</IconButton>
									</Tooltip>
								)}
							</>
						</Box>
					</Paper>

					<Box
						className={classes.editorWrapper}
						style={{ display: viewMode === 0 ? "block" : "none" }}
					>
						<AceEditor
							ref={editorRef}
							mode="html"
							theme="textmate"
							onChange={handleAceChange}
							value={value || ""}
							name="html-editor-container"
							editorProps={{
								$blockScrolling: true,
							}}
							setOptions={{
								enableBasicAutocompletion: true,
								enableLiveAutocompletion: true,
								enableSnippets: true,
								showLineNumbers: true,
								tabSize: 2,
								useWorker: false,
							}}
							width="100%"
							height="300px"
							fontSize={14}
							showPrintMargin={false}
							readOnly={isDisabled}
							placeholder={editorInfo.placeholder}
							onBlur={handleBlur}
							onLoad={(editor) => {
								const textarea = editor.textInput.getElement();
								if (textarea) {
									textarea.setAttribute("name", "html-editor");
									textarea.setAttribute("id", "html-editor");
								}
							}}
						/>
					</Box>

					{viewMode === 1 && (
						<Paper className={classes.previewContainer}>
							<div
								className={classes.htmlPreview}
								dangerouslySetInnerHTML={{
									__html: value || `<p>${resources["NoContentToPreview"]}</p>`,
								}}
							/>
						</Paper>
					)}

					<FullScreenEditor
						open={fullScreenOpen}
						onClose={handleCloseFullScreen}
						value={value}
						onChange={handleAceChange}
						onSave={handleCloseFullScreen}
						resources={resources}
						dealerGroupId={dealerGroupId}
						markPreviewAsViewed={markPreviewAsViewed}
						showPreview={showPreview}
						useCase={useCase}
						messageType={messageType}
					/>
				</Box>
			) : (
				<TextareaWithAutocompletion
					value={value || ""}
					onChange={onChange}
					onBlur={onBlur}
					placeholder={editorInfo.placeholder}
					disabled={isDisabled}
					error={error}
					useCase={useCase}
					messageType={messageType}
					resources={resources}
				/>
			)}

			<Typography
				className={classes.errorText}
				style={{
					color: error ? "#C4455B" : "#0000008a",
					marginTop: 4,
				}}
			>
				{resources["BodyIsRequired"]}
			</Typography>

			{viewMode === 0 && (
				<MappableFieldsInfo
					availableFields={availableFields}
					resources={resources}
					chipClass={classes.chip}
				/>
			)}

			{mediaLibraryOpen && (
				<MediaLibraryModal
					open={mediaLibraryOpen}
					onClose={handleCloseMediaLibrary}
					module="messageTemplates"
					resources={resources}
					dealerGroupId={dealerGroupId}
				/>
			)}
		</Box>
	);
};

export default MessageBodyEditor;
